import {
  ArrowBack,
  ArrowForward,
  CheckCircle,
  Close,
  FolderOpen,
  HelpOutline,
  PhotoLibrary,
  ShoppingBag,
  SupportAgent,
} from '@mui/icons-material';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogContent,
  IconButton,
  LinearProgress,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import api from '../../api';
import { COLORS } from '../../theme';
import { getEffectiveUserId, getMasterToken } from '../../utils/auth';
import { Diamond, GridOverlay, Sparkle } from '../landing/LandingShapes';

// ─── Configuração dos tours por página ──────────────────────────────────────

const buildTourSteps = (pathname) => {
  const path = pathname.replace(/\/+$/, '') || '/';

  if (path === '/user/dashboard') {
    return [
      {
        selector: '[data-tour="dashboard-hero"]',
        title: 'Seu painel',
        text: 'Esta é a visão geral da sua conta. O resumo mostra o que está em produção, quanto está em aberto e quando é a próxima entrega.',
      },
      {
        selector: '[data-tour="dashboard-actions"]',
        title: 'O que precisa de você',
        text: 'Pagamentos em aberto, seleção de fotos e prazos de retirada aparecem aqui com o botão da ação. Quando não há nada pendente, a lista mostra “Tudo em dia”.',
      },
      {
        selector: '[data-tour="dashboard-stats"]',
        title: 'Seus números',
        text: 'Investimento, valores em aberto, projetos entregues e ticket médio ficam sempre atualizados.',
      },
      {
        selector: '[data-tour="dashboard-chart"]',
        title: 'Investimento e produção',
        text: 'Use o seletor de período para ver os gastos por mês e acompanhe em “Sua produção” em que etapa cada pedido está.',
      },
    ];
  }

  if (path === '/user/orders') {
    return [
      {
        selector: '[data-tour="orders-page"]',
        title: 'Meus pedidos',
        text: 'Todos os seus pedidos aparecem aqui, com status e valor a pagar. Use os filtros e a paginação para encontrar qualquer trabalho.',
      },
      {
        selector: '[data-tour="orders-list"]',
        title: 'Lista de pedidos',
        text: 'Cada linha mostra a etapa, o pagamento, o valor e o botão para abrir as fotos e arquivos.',
      },
      {
        selector: '[data-tour="order-card"]',
        title: 'Ação principal: arquivos',
        text: 'O botão laranja abre as fotos e arquivos do pedido. Pagamento e detalhes ficam ao lado.',
      },
    ];
  }

  if (path.startsWith('/user/pedido/')) {
    return [
      {
        selector: '[data-tour="pedido-resumo"]',
        title: 'Resumo do pedido',
        text: 'Aqui ficam o status, as datas, os valores e os serviços contratados. Confira antes de baixar os arquivos.',
      },
      {
        selector: '[data-tour="pedido-arquivos"]',
        title: 'Arquivos do pedido',
        text: 'As fotos e vídeos liberados aparecem nesta área. Clique em uma imagem para ampliar e use o botão de download quando o pagamento estiver confirmado.',
      },
      {
        selector: '[data-tour="pedido-selecao"]',
        title: 'Seleção de fotos',
        text: 'Este pedido tem uma pasta de seleção: abra a pasta, marque no mínimo 5 fotos e clique em “Confirmar Seleção” ao terminar.',
      },
    ];
  }

  if (path === '/user/payments') {
    return [
      {
        selector: '[data-tour="payments-page"]',
        title: 'Pagamentos',
        text: 'Acompanhe faturas e pagamentos dos seus pedidos. Você pode buscar por número do pedido ou valor.',
      },
      {
        selector: '[data-tour="payments-list"]',
        title: 'Faturas',
        text: 'Cada cartão mostra vencimento, status e valor. Use os filtros de tipo, status e ordenação para localizar rapidamente.',
      },
    ];
  }

  if (path === '/user/settings') {
    return [
      {
        selector: '[data-tour="settings-page"]',
        title: 'Segurança da conta',
        text: 'Aqui você altera sua senha. Use uma senha forte e diferente de outros sites.',
      },
      {
        selector: '[data-tour="settings-form"]',
        title: 'Alterar senha',
        text: 'Informe a senha atual, a nova senha e confirme. A alteração é aplicada na hora.',
      },
    ];
  }

  if (path === '/user/help') {
    return [
      {
        selector: '[data-tour="help-page"]',
        title: 'Central de ajuda',
        text: 'Reunimos os canais de atendimento e as dúvidas mais comuns sobre prazos, pagamentos e arquivos.',
      },
      {
        selector: '[data-tour="help-channels"]',
        title: 'Fale com a gente',
        text: 'Escolha WhatsApp, e-mail ou consulte a base de conhecimento.',
      },
      {
        selector: '[data-tour="help-faq"]',
        title: 'Perguntas frequentes',
        text: 'Clique em uma pergunta para ver a resposta completa.',
      },
    ];
  }

  if (path.startsWith('/user/conhecimento')) {
    return [
      {
        selector: '[data-tour="user-nav"]',
        title: 'Navegação',
        text: 'Use a barra inferior para alternar entre painel, pedidos, pagamentos, alertas e perfil. Ajustes, ajuda e sair ficam no menu ⋯ do topo.',
      },
    ];
  }

  if (path.startsWith('/user/') && path !== '/user') {
    return [
      {
        selector: '[data-tour="user-nav"]',
        title: 'Navegação',
        text: 'Use a barra inferior (ou o menu lateral no computador) para voltar aos pedidos. Ajustes e ajuda ficam no menu ⋯ do topo.',
      },
      {
        selector: '[data-tour="profile-card"]',
        title: 'Seu perfil',
        text: 'Aqui você confere seus dados de contato. Para trocar seu avatar, clique na foto do menu lateral.',
      },
    ];
  }

  return [
    {
      selector: '[data-tour="user-nav"]',
      title: 'Sua área do cliente',
      text: 'Use a barra inferior para acompanhar pedidos, acessar arquivos, pagamentos e alertas. Ajustes e ajuda ficam no menu ⋯ do topo.',
    },
    {
      selector: '[data-tour="profile-card"]',
      title: 'Seu perfil',
      text: 'Aqui você confere seus dados de contato. Para trocar seu avatar, clique na foto do menu lateral.',
    },
  ];
};

// ─── Persistência do guia ───────────────────────────────────────────────────
// O estado oficial fica no backend (Cliente.onboarding). O localStorage é só
// um cache local, para o guia não "piscar" antes da API responder e para
// funcionar caso a API esteja indisponível.

const getWelcomeCacheKey = (userId) => `zoompro_user_welcome_v1_${userId}`;
const getTourCacheKey = (userId) => `zoompro_user_tour_once_v1_${userId}`;

// Versões antigas gravavam um registro por página ("zoompro_user_tour_<page>_v1_<id>").
const hasLegacyTourSeen = (userId) => {
  try {
    return Object.keys(localStorage).some(
      (key) => key.startsWith('zoompro_user_tour_') && key.endsWith(`_${userId}`)
    );
  } catch (e) {
    return false;
  }
};

const readOnboardingCache = (userId) => {
  try {
    return {
      welcomeSeen: localStorage.getItem(getWelcomeCacheKey(userId)) === '1',
      tourSeen: localStorage.getItem(getTourCacheKey(userId)) === '1' || hasLegacyTourSeen(userId),
    };
  } catch (e) {
    return { welcomeSeen: false, tourSeen: false };
  }
};

const writeOnboardingCache = (userId, { welcomeSeen, tourSeen }) => {
  try {
    if (welcomeSeen) localStorage.setItem(getWelcomeCacheKey(userId), '1');
    if (tourSeen) localStorage.setItem(getTourCacheKey(userId), '1');
  } catch (e) {
    /* storage indisponível */
  }
};

const clearOnboardingCache = (userId) => {
  try {
    localStorage.removeItem(getWelcomeCacheKey(userId));
    localStorage.removeItem(getTourCacheKey(userId));
  } catch (e) {
    /* storage indisponível */
  }
};

// Encontra o primeiro elemento visível (a sidebar desktop fica oculta no mobile,
// então não podemos usar apenas querySelector).
const findTourTarget = (selector) => {
  const nodes = Array.from(document.querySelectorAll(selector));
  return (
    nodes.find((element) => {
      const rect = element.getBoundingClientRect();
      const style = window.getComputedStyle(element);
      return (
        rect.width > 0 &&
        rect.height > 0 &&
        style.display !== 'none' &&
        style.visibility !== 'hidden'
      );
    }) || null
  );
};

const WELCOME_FEATURES = [
  {
    icon: ShoppingBag,
    title: 'Acompanhe seus pedidos',
    text: 'Status, datas, valores e detalhes de cada produção.',
  },
  {
    icon: FolderOpen,
    title: 'Acesse seus arquivos',
    text: 'Fotos e vídeos liberados ficam disponíveis depois do pagamento.',
  },
  {
    icon: PhotoLibrary,
    title: 'Selecione suas fotos',
    text: 'Quando houver pasta de seleção, marque as favoritas e confirme.',
  },
  {
    icon: SupportAgent,
    title: 'Fale com o estúdio',
    text: 'Precisa de ajuda? A central de ajuda tem WhatsApp e e-mail.',
  },
];

// ─── Componente ─────────────────────────────────────────────────────────────

const UserOnboarding = () => {
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const userId = String(getEffectiveUserId() || 'anon');
  const masterAccess = Boolean(getMasterToken());

  const steps = useMemo(() => buildTourSteps(location.pathname), [location.pathname]);

  const [seen, setSeen] = useState(() => readOnboardingCache(userId));
  const [stateLoaded, setStateLoaded] = useState(false);
  const [welcomeOpen, setWelcomeOpen] = useState(false);
  const [tourOpen, setTourOpen] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const [rect, setRect] = useState(null);

  // Atualiza o cache local na hora e registra a decisão no backend.
  const persistSeen = useCallback(
    async (payload) => {
      setSeen((current) => {
        const next = { ...current, ...payload };
        // Na visualização administrativa não grava cache do cliente.
        if (!masterAccess) writeOnboardingCache(userId, next);
        return next;
      });

      if (masterAccess) return;

      try {
        await api.post('/user/onboarding/seen', payload);
      } catch (error) {
        console.warn('Não foi possível registrar o guia no servidor:', error?.message);
      }
    },
    [masterAccess, userId]
  );

  const closeTour = useCallback(() => {
    setTourOpen(false);
    persistSeen({ welcomeSeen: true, tourSeen: true });
  }, [persistSeen]);

  const nextStep = () => {
    if (stepIndex < steps.length - 1) setStepIndex((index) => index + 1);
    else closeTour();
  };

  const prevStep = () => setStepIndex((index) => Math.max(0, index - 1));

  // Busca no backend o estado do guia (fonte oficial). O cache local é usado
  // apenas como fallback/para evitar que o guia "pisque" antes da resposta.
  useEffect(() => {
    let cancelled = false;
    const cached = readOnboardingCache(userId);

    setSeen(cached);
    setStateLoaded(false);

    // Em modo de visualização administrativa não dispara nem grava o guia.
    if (masterAccess) {
      return () => {
        cancelled = true;
      };
    }

    api
      .get('/user/onboarding')
      .then(({ data }) => {
        if (cancelled) return;
        const merged = {
          welcomeSeen: Boolean(data?.welcomeSeen) || cached.welcomeSeen,
          tourSeen: Boolean(data?.tourSeen) || cached.tourSeen,
        };
        setSeen(merged);
        writeOnboardingCache(userId, merged);
      })
      .catch(() => {
        if (!cancelled) setSeen(cached);
      })
      .finally(() => {
        if (!cancelled) setStateLoaded(true);
      });

    return () => {
      cancelled = true;
    };
  }, [masterAccess, userId]);

  // Abre a introdução apenas se o cliente ainda não viu o guia
  useEffect(() => {
    if (!stateLoaded || seen.welcomeSeen || welcomeOpen || tourOpen) return;
    const timer = setTimeout(() => setWelcomeOpen(true), 900);
    return () => clearTimeout(timer);
  }, [stateLoaded, seen.welcomeSeen, welcomeOpen, tourOpen]);

  // Inicia o tour automaticamente uma única vez — até o cliente concluir,
  // pular ou escolher "Explorar sozinho". Depois disso ele só reaparece
  // quando o cliente clicar em "Como usar a plataforma".
  useEffect(() => {
    if (!stateLoaded || seen.tourSeen || !seen.welcomeSeen || welcomeOpen || tourOpen) return;
    if (steps.length === 0) return;

    const timer = setTimeout(() => {
      setStepIndex(0);
      setTourOpen(true);
    }, 600);
    return () => clearTimeout(timer);
  }, [stateLoaded, seen.tourSeen, seen.welcomeSeen, welcomeOpen, tourOpen, steps.length]);

  // Botão "Como usar a plataforma" (sidebar, menu mobile ou botão flutuante):
  // reinicia o registro no backend para o guia reaparecer.
  useEffect(() => {
    const handler = () => {
      if (!masterAccess) {
        clearOnboardingCache(userId);
        api.post('/user/onboarding/reset').catch(() => {});
      }

      setSeen({ welcomeSeen: false, tourSeen: false });
      setStateLoaded(true);
      setStepIndex(0);
      setTourOpen(false);
      setWelcomeOpen(true);
    };

    window.addEventListener('zoompro:open-help', handler);
    return () => window.removeEventListener('zoompro:open-help', handler);
  }, [masterAccess, userId]);

  // Localiza e acompanha o elemento destacado pelo passo atual
  useEffect(() => {
    if (!tourOpen) {
      setRect(null);
      return;
    }

    const currentStep = steps[stepIndex];
    if (!currentStep) {
      closeTour();
      return;
    }

    let attempts = 0;
    let retryTimer;
    let interval;
    let scrollTimer;

    const updateRect = () => {
      const element = findTourTarget(currentStep.selector);
      if (element) setRect(element.getBoundingClientRect());
    };

    const locate = () => {
      const element = findTourTarget(currentStep.selector);
      if (!element) {
        attempts += 1;
        if (attempts < 20) {
          retryTimer = setTimeout(locate, 350);
        } else if (stepIndex < steps.length - 1) {
          setStepIndex((index) => index + 1);
        } else {
          closeTour();
        }
        return;
      }

      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      updateRect();
      scrollTimer = setTimeout(updateRect, 420);
      interval = setInterval(updateRect, 450);
    };

    locate();
    window.addEventListener('resize', updateRect);
    window.addEventListener('scroll', updateRect, true);

    return () => {
      clearTimeout(retryTimer);
      clearTimeout(scrollTimer);
      clearInterval(interval);
      window.removeEventListener('resize', updateRect);
      window.removeEventListener('scroll', updateRect, true);
    };
  }, [tourOpen, stepIndex, steps, closeTour]);

  const iniciarTour = () => {
    persistSeen({ welcomeSeen: true });
    setWelcomeOpen(false);

    if (steps.length === 0) return;

    setStepIndex(0);
    setTourOpen(true);
  };

  const explorarSozinho = () => {
    // Fechou a introdução sem seguir o tour: não mostra o guia de novo sozinho.
    persistSeen({ welcomeSeen: true, tourSeen: true });
    setWelcomeOpen(false);
  };

  const step = steps[stepIndex];
  const ultimoPasso = stepIndex >= steps.length - 1;

  const viewportH = typeof window !== 'undefined' ? window.innerHeight : 800;
  const viewportW = typeof window !== 'undefined' ? window.innerWidth : 1200;
  const tooltipWidth = Math.min(360, viewportW - 32);
  const tooltipTop = rect
    ? rect.bottom + 260 <= viewportH
      ? rect.bottom + 18
      : Math.max(16, rect.top - 250)
    : 0;
  const tooltipLeft = rect
    ? Math.min(
        Math.max(16, rect.left + rect.width / 2 - tooltipWidth / 2),
        viewportW - tooltipWidth - 16
      )
    : 0;

  return (
    <>
      {/* Botão flutuante para reabrir o guia a qualquer momento */}
      <Tooltip title="Como usar a plataforma" arrow placement="left">
        <Box
          component="button"
          type="button"
          onClick={() => window.dispatchEvent(new CustomEvent('zoompro:open-help'))}
          aria-label="Abrir guia de uso da plataforma"
          sx={{
            position: 'fixed',
            right: { xs: 16, md: 24 },
            bottom: { xs: 'calc(16px + env(safe-area-inset-bottom, 0px))', md: 24 },
            zIndex: 1250,
            width: 52,
            height: 52,
            borderRadius: '50%',
            border: `2px solid ${COLORS.dark}`,
            bgcolor: COLORS.orange,
            color: COLORS.white,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            boxShadow: '0 10px 24px rgba(235,91,37,0.4)',
            transition: 'transform 0.2s ease',
            '&:hover': { transform: 'scale(1.06)' },
          }}
        >
          <HelpOutline />
        </Box>
      </Tooltip>

      {/* Introdução exibida no primeiro acesso */}
      <Dialog
        open={welcomeOpen}
        onClose={explorarSozinho}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: COLORS.cream,
            border: `2px solid ${COLORS.dark}`,
            backgroundImage: 'none',
          },
        }}
      >
        <DialogContent sx={{ p: { xs: 2.5, sm: 4 }, position: 'relative' }}>
          <GridOverlay />
          <IconButton
            onClick={explorarSozinho}
            aria-label="Fechar introdução"
            sx={{ position: 'absolute', top: 8, right: 8, color: COLORS.dark, zIndex: 2 }}
          >
            <Close />
          </IconButton>

          <Box sx={{ position: 'relative', zIndex: 1, textAlign: 'center' }}>
            <Box
              sx={{
                width: 66,
                height: 66,
                borderRadius: '20px',
                bgcolor: COLORS.dark,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 2,
              }}
            >
              <Sparkle size={34} color={COLORS.orange} />
            </Box>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1,
                mb: 1,
              }}
            >
              <Diamond size={10} color={COLORS.orange} />
              <Typography
                sx={{
                  color: COLORS.orange,
                  fontWeight: 800,
                  letterSpacing: 1.5,
                  fontSize: '0.7rem',
                }}
              >
                PRIMEIROS PASSOS
              </Typography>
            </Box>
            <Typography
              sx={{
                fontFamily: '"Poppins", sans-serif',
                fontWeight: 900,
                fontSize: { xs: '1.45rem', sm: '1.8rem' },
                lineHeight: 1.15,
                color: COLORS.dark,
              }}
            >
              Bem-vindo à sua área do cliente
            </Typography>
            <Typography
              sx={{ color: 'rgba(28,27,25,0.7)', mt: 1.2, fontSize: '0.92rem', lineHeight: 1.65 }}
            >
              Em menos de um minuto você aprende onde acompanhar pedidos, baixar arquivos e
              selecionar suas fotos.
            </Typography>
          </Box>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
              gap: 1.4,
              my: 3,
              position: 'relative',
              zIndex: 1,
            }}
          >
            {WELCOME_FEATURES.map(({ icon: Icon, title, text }) => (
              <Box
                key={title}
                sx={{
                  p: 1.6,
                  borderRadius: '14px',
                  border: `1px solid ${COLORS.line}`,
                  bgcolor: 'rgba(255,255,255,0.7)',
                  display: 'flex',
                  gap: 1.2,
                  alignItems: 'flex-start',
                }}
              >
                <Box
                  sx={{
                    width: 34,
                    height: 34,
                    flexShrink: 0,
                    borderRadius: '10px',
                    bgcolor: 'rgba(235,91,37,0.12)',
                    color: COLORS.orange,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Icon sx={{ fontSize: 19 }} />
                </Box>
                <Box>
                  <Typography
                    sx={{
                      fontFamily: '"Poppins", sans-serif',
                      fontWeight: 700,
                      fontSize: '0.85rem',
                      color: COLORS.dark,
                    }}
                  >
                    {title}
                  </Typography>
                  <Typography
                    sx={{
                      color: 'rgba(28,27,25,0.62)',
                      fontSize: '0.76rem',
                      lineHeight: 1.5,
                      mt: 0.3,
                    }}
                  >
                    {text}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>

          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column-reverse', sm: 'row' },
              gap: 1.2,
              justifyContent: 'center',
              position: 'relative',
              zIndex: 1,
            }}
          >
            <Button onClick={explorarSozinho} sx={{ color: COLORS.dark, fontWeight: 700 }}>
              Explorar sozinho
            </Button>
            <Button
              onClick={iniciarTour}
              variant="contained"
              startIcon={<CheckCircle />}
              sx={{
                bgcolor: COLORS.orange,
                color: COLORS.white,
                fontWeight: 800,
                borderRadius: '50px',
                px: 3,
                '&:hover': { bgcolor: COLORS.orangeDeep },
              }}
            >
              Começar tour
            </Button>
          </Box>
          <Typography
            sx={{
              display: 'block',
              textAlign: 'center',
              mt: 2,
              color: 'rgba(28,27,25,0.5)',
              fontSize: '0.72rem',
              position: 'relative',
              zIndex: 1,
            }}
          >
            Você pode rever este guia pelo botão “?” a qualquer momento.
          </Typography>
        </DialogContent>
      </Dialog>

      {/* Tour guiado com destaque no elemento da página */}
      {tourOpen && step && (
        <Box sx={{ position: 'fixed', inset: 0, zIndex: 2000, pointerEvents: 'none' }}>
          <Box
            onClick={(event) => event.stopPropagation()}
            sx={{
              position: 'fixed',
              inset: 0,
              zIndex: 2000,
              pointerEvents: 'auto',
              bgcolor: 'transparent',
            }}
          />

          {rect ? (
            <Box
              aria-hidden
              sx={{
                position: 'fixed',
                top: Math.max(8, rect.top - 8),
                left: Math.max(8, rect.left - 8),
                width: rect.width + 16,
                height: rect.height + 16,
                borderRadius: '18px',
                border: `2px solid ${COLORS.orange}`,
                boxShadow: '0 0 0 9999px rgba(18,17,16,0.74)',
                zIndex: 2001,
                pointerEvents: 'none',
                transition: 'all 0.25s ease',
              }}
            />
          ) : (
            <Box
              sx={{
                position: 'fixed',
                inset: 0,
                zIndex: 2001,
                bgcolor: 'rgba(18,17,16,0.74)',
                pointerEvents: 'none',
              }}
            />
          )}

          <Box
            role="dialog"
            aria-modal="true"
            aria-label={`Passo ${stepIndex + 1} de ${steps.length}: ${step.title}`}
            sx={{
              position: 'fixed',
              zIndex: 2003,
              pointerEvents: 'auto',
              bgcolor: COLORS.cream,
              color: COLORS.dark,
              borderRadius: '20px',
              border: `2px solid ${COLORS.dark}`,
              boxShadow: '0 24px 60px rgba(0,0,0,0.45)',
              p: { xs: 2.2, sm: 2.6 },
              ...(isMobile || !rect
                ? {
                    left: 16,
                    right: 16,
                    bottom: 'calc(16px + env(safe-area-inset-bottom, 0px))',
                    width: 'auto',
                  }
                : { top: tooltipTop, left: tooltipLeft, width: tooltipWidth }),
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1,
                mb: 1.4,
              }}
            >
              <Chip
                label={`${stepIndex + 1} de ${steps.length}`}
                size="small"
                sx={{ bgcolor: COLORS.orange, color: COLORS.white, fontWeight: 800, height: 24 }}
              />
              <IconButton
                onClick={closeTour}
                size="small"
                aria-label="Fechar tour"
                sx={{ color: COLORS.dark }}
              >
                <Close fontSize="small" />
              </IconButton>
            </Box>

            <Typography
              sx={{
                fontFamily: '"Poppins", sans-serif',
                fontWeight: 900,
                fontSize: '1.08rem',
                lineHeight: 1.25,
              }}
            >
              {step.title}
            </Typography>
            <Typography
              sx={{ color: 'rgba(28,27,25,0.72)', fontSize: '0.86rem', lineHeight: 1.65, mt: 0.8 }}
            >
              {step.text}
            </Typography>

            <LinearProgress
              variant="determinate"
              value={((stepIndex + 1) / steps.length) * 100}
              sx={{
                my: 2,
                height: 5,
                borderRadius: 3,
                bgcolor: 'rgba(235,91,37,0.15)',
                '& .MuiLinearProgress-bar': { bgcolor: COLORS.orange, borderRadius: 3 },
              }}
            />

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Button
                onClick={closeTour}
                size="small"
                sx={{ color: 'rgba(28,27,25,0.6)', fontWeight: 700, px: 1 }}
              >
                Pular
              </Button>
              <Box sx={{ flex: 1 }} />
              {stepIndex > 0 && (
                <Button
                  onClick={prevStep}
                  size="small"
                  startIcon={<ArrowBack fontSize="small" />}
                  sx={{
                    color: COLORS.dark,
                    fontWeight: 700,
                    border: `1px solid ${COLORS.line}`,
                    borderRadius: '50px',
                    px: 1.6,
                  }}
                >
                  Voltar
                </Button>
              )}
              <Button
                onClick={nextStep}
                size="small"
                variant="contained"
                endIcon={
                  ultimoPasso ? <CheckCircle fontSize="small" /> : <ArrowForward fontSize="small" />
                }
                sx={{
                  bgcolor: COLORS.orange,
                  color: COLORS.white,
                  fontWeight: 800,
                  borderRadius: '50px',
                  px: 2,
                  '&:hover': { bgcolor: COLORS.orangeDeep },
                }}
              >
                {ultimoPasso ? 'Concluir' : 'Próximo'}
              </Button>
            </Box>
          </Box>
        </Box>
      )}
    </>
  );
};

export default UserOnboarding;
